import Chevron from 'src/components/dashboard/components/navigationMenu/components/chevron';
import { CreditStatusTableData } from 'src/services/models/credit.service';
import { CreditStatusTableSortType } from 'src/views/pages/credit/status';
import { formatDate } from 'src/utils/formatDate';
import { getSubmittedByLabel } from 'src/utils/credit.helpers';
import { isSiteCanadian } from 'src/utils/helpers';
import { useLocalization } from 'src/utils/useLocalization';
import { Dispatch, memo, SetStateAction } from 'react';
import { StatusDot, statusToStatusClass } from '../../../ui/statusDot';

import './style.scss';

export interface ITableContents {
    items: CreditStatusTableData[];
    setDetail: Dispatch<
        SetStateAction<{ applicationId: string; applicationNumber: number }>
    >;
    setSortType: Dispatch<SetStateAction<number>>;
    setSortOrder: Dispatch<SetStateAction<boolean>>;
    sortType: number;
    sortOrder: boolean;
}

export interface CreditStatusTableItemData {
    data: CreditStatusTableData;
    setDetail: Dispatch<
        SetStateAction<{ applicationId: string; applicationNumber: number }>
    >;
}

interface ITableHeaderItem {
    title: string;
    sortHandler: Function;
    sortType: number;
    sortOrder: boolean;
    colType: number;
}

const TableItemComponent: React.FC<CreditStatusTableItemData> = ({
    data,
    setDetail,
}) => {
    const {
        name,
        applicationNumber,
        status,
        collateral,
        sourceSystem,
        submittedTime,
        submittedBy,
        key,
    } = data;
    const statusCSSClassName = statusToStatusClass(status);
    const { creditStatusFilterResponseToOptionMap, creditSearch } =
        useLocalization();
    return (
        <div className='row'>
            <div className='table-row col-1'>
                <span className='list-label'>
                    {creditSearch.TABLE_HEADER_APPLICANT_NAME}
                </span>
                <span
                    className='underline'
                    onClick={() =>
                        setDetail({
                            applicationId: key,
                            applicationNumber: applicationNumber,
                        })
                    }
                    aria-hidden={'true'}
                >
                    {name}
                </span>
            </div>
            <div className='table-row col-2'>
                <span className='list-label'>
                    {creditSearch.TABLE_HEADER_APPLICATION_NUMBER}
                </span>
                <span>{applicationNumber}</span>
            </div>
            <div className='table-row col-3'>
                <span className='list-label'>
                    {creditSearch.TABLE_HEADER_STATUS}
                </span>
                <div className='status'>
                    <StatusDot color={statusCSSClassName} />
                    <span className='dot-label'>
                        {creditStatusFilterResponseToOptionMap[status]}
                    </span>
                </div>
            </div>
            <div className='table-row credit-table-col-4'>
                <span className='list-label'>
                    {creditSearch.TABLE_HEADER_COLLATERAL}
                </span>
                <span>{collateral}</span>
            </div>
            <div className='table-row col-5'>
                <span className='list-label'>
                    {creditSearch.TABLE_HEADER_SUBMITTED_DATE}
                </span>
                <span>{formatDate(submittedTime)}</span>
            </div>
            <div className='table-row col-6'>
                <span className='list-label'>
                    {creditSearch.TABLE_HEADER_SUBMITTED_BY}
                </span>
                <span>
                    {getSubmittedByLabel(
                        sourceSystem,
                        submittedBy,
                        isSiteCanadian(),
                    )}
                </span>
            </div>
        </div>
    );
};

const TableItem = memo(TableItemComponent);

const ChevronButton: React.FC<{ rotate: string; sortHandler: Function }> = ({
    rotate,
    sortHandler,
}) => {
    const chevronColor = '#363b49';
    return (
        <span
            onClick={() => {
                sortHandler();
            }}
            aria-hidden={'true'}
        >
            <Chevron
                className={`accordion__icon ${rotate}`}
                width={10}
                fill={chevronColor}
            />
        </span>
    );
};

const HeaderItem: React.FC<ITableHeaderItem> = ({
    title,
    sortHandler,
    sortOrder,
    sortType,
    colType,
}) => {
    const isSortByThisCol = sortType === colType;
    const shouldRenderUpCol = isSortByThisCol && sortOrder;
    const rotateClass = shouldRenderUpCol ? 'rotate-down' : 'rotate-up';
    return (
        <>
            <span className='u-text-truncate'>{title}</span>
            <span className='table-header__order'>
                <ChevronButton
                    rotate={rotateClass}
                    sortHandler={() => {
                        sortHandler(colType, true);
                    }}
                />
                {!isSortByThisCol ? (
                    <ChevronButton
                        rotate='rotate-down'
                        sortHandler={() => {
                            sortHandler(colType, false);
                        }}
                    />
                ) : null}
            </span>
        </>
    );
};

export const Table: React.FC<ITableContents> = ({
    items,
    setDetail,
    setSortOrder,
    setSortType,
    sortOrder,
    sortType,
}) => {
    const { creditSearch } = useLocalization();
    const applySort = (property: number, order = true) => {
        if (sortType === property) {
            setSortOrder(!sortOrder);
        } else {
            setSortType(property);
            setSortOrder(order);
        }
    };
    return (
        <div className='table-credit'>
            <div className='row header-row hidden-in-list'>
                <div className='table-header col-1'>
                    <HeaderItem
                        title={creditSearch.TABLE_HEADER_APPLICANT_NAME}
                        colType={CreditStatusTableSortType.APPLICANT_NAME}
                        sortHandler={applySort}
                        sortOrder={sortOrder}
                        sortType={sortType}
                    />
                </div>
                <div className='table-header col-2'>
                    <HeaderItem
                        title={creditSearch.TABLE_HEADER_APPLICATION_NUMBER}
                        colType={CreditStatusTableSortType.APPLICATION_NUMBER}
                        sortHandler={applySort}
                        sortOrder={sortOrder}
                        sortType={sortType}
                    />
                </div>
                <div className='table-header col-3'>
                    <HeaderItem
                        title={creditSearch.TABLE_HEADER_STATUS}
                        colType={CreditStatusTableSortType.STATUS}
                        sortHandler={applySort}
                        sortOrder={sortOrder}
                        sortType={sortType}
                    />
                </div>
                <div className='table-header col-4'>
                    <HeaderItem
                        title={creditSearch.TABLE_HEADER_COLLATERAL}
                        colType={CreditStatusTableSortType.COLLATERAL}
                        sortHandler={applySort}
                        sortOrder={sortOrder}
                        sortType={sortType}
                    />
                </div>
                <div className='table-header col-5'>
                    <HeaderItem
                        title={creditSearch.TABLE_HEADER_SUBMITTED_BY}
                        colType={CreditStatusTableSortType.SUBMITTED_DATE}
                        sortHandler={applySort}
                        sortOrder={sortOrder}
                        sortType={sortType}
                    />
                </div>
                <div className='table-header col-6'>
                    <HeaderItem
                        title={creditSearch.TABLE_HEADER_SUBMITTED_DATE}
                        colType={CreditStatusTableSortType.SUBMITTED_BY}
                        sortHandler={applySort}
                        sortOrder={sortOrder}
                        sortType={sortType}
                    />
                </div>
            </div>
            {items.map((item) => (
                <TableItem data={item} key={item.key} setDetail={setDetail} />
            ))}
        </div>
    );
};

export default Table;
